import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { Spinner, Modal, Button } from '../../components/shared/UI';
import API from '../../utils/apiClient';

const NAV = [
  { to:'/super-admin/dashboard',  icon:'🏠', label:'Dashboard'  },
  { icon:'👥', label:'Students', children:[
    { to:'/super-admin/students',      label:'All Students' },
  ]},
  { icon:'👨‍🏫', label:'Teachers', children:[
    { to:'/super-admin/teachers',      label:'All Teachers' },
  ]},
  { icon:'🏫', label:'Cohorts', children:[
    { to:'/super-admin/cohorts',       label:'All Cohorts'  },
    { to:'/super-admin/cohorts/create',label:'Create Cohort'},
  ]},
  { to:'/super-admin/assignments', icon:'🔗', label:'Assignments'},
  { to:'/super-admin/timetable',   icon:'📅', label:'Timetable'  },
  { to:'/super-admin/attendance',  icon:'✅', label:'Attendance' },
  { to:'/super-admin/payments',    icon:'💳', label:'Payments'   },
  { to:'/super-admin/pricing',     icon:'💰', label:'Pricing'    },
];

function SidebarItem({ item }) {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  if (item.children) {
    const any = item.children.some(c => loc.pathname.startsWith(c.to));
    return (
      <div>
        <button onClick={() => setOpen(p=>!p)} className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${any?'bg-brand-50 text-brand-600':'text-gray-600 hover:bg-gray-50'}`}>
          <span className="flex items-center gap-3"><span>{item.icon}</span>{item.label}</span>
          <span className="text-xs text-gray-400">{open||any?'▾':'▸'}</span>
        </button>
        {(open||any) && (
          <div className="ml-9 mt-0.5 space-y-0.5">
            {item.children.map(c=>(
              <Link key={c.to} to={c.to} className={`block px-3 py-2 rounded-lg text-xs font-medium transition-colors ${loc.pathname===c.to?'bg-brand-500 text-white':'text-gray-500 hover:bg-gray-100'}`}>{c.label}</Link>
            ))}
          </div>
        )}
      </div>
    );
  }
  const active = loc.pathname === item.to;
  return (
    <Link to={item.to} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${active?'bg-brand-500 text-white shadow-sm':'text-gray-600 hover:bg-gray-50'}`}>
      <span>{item.icon}</span>{item.label}
    </Link>
  );
}

export function SuperAdminShell({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="flex min-h-screen bg-gray-50">
      <aside className="w-60 bg-white border-r border-gray-200 flex-shrink-0 flex flex-col fixed h-full z-30">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className="w-7 h-5 flex flex-col rounded-sm overflow-hidden border border-gray-300 flex-shrink-0">
              <div className="flex-1 bg-de-black"/><div className="flex-1 bg-brand-500"/><div className="flex-1 bg-gold-400"/>
            </div>
            <p className="text-sm font-bold"><span style={{color:'#FFCE00'}}>Deutsch</span>Bridge</p>
          </div>
          <p className="text-xs font-semibold text-brand-500 mt-1 pl-9">Super Admin</p>
        </div>
        <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {NAV.map((item,i)=><SidebarItem key={i} item={item}/>)}
        </nav>
        <div className="p-3 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-full bg-brand-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">{user?.name?.charAt(0)||'A'}</div>
              <div className="min-w-0"><p className="text-xs font-bold truncate">{user?.name}</p><p className="text-xs text-gray-400">Super Admin</p></div>
            </div>
            <button onClick={()=>{logout();navigate('/login');}} className="text-xs text-gray-400 hover:text-brand-500 ml-2">↩</button>
          </div>
        </div>
      </aside>
      <div className="ml-60 flex-1 flex flex-col">
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-20">
          <div>
            <h1 className="text-base font-bold text-de-black">{title}</h1>
            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </header>
        <main className="flex-1 p-6">{children}</main>
      </div>
    </div>
  );
}

export default function SuperAdminDashboard() {
  const { addToast } = useToast();
  const [stats, setStats]       = useState(null);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [tModal, setTModal]     = useState(false);
  const [sModal, setSModal]     = useState(false);
  const [tf, setTf] = useState({name:'',email:'',phone:'',assignedLevels:['A1','A2','B1','B2']});
  const [sf, setSf] = useState({name:'',email:'',phone:'',level:'A1',scholarshipType:'None',notes:''});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      API.get('/super-admin/stats').then(r=>r.data).catch(()=>null),
      API.get('/super-admin/students').then(r=>r.data).catch(()=>({students:[]})),
      API.get('/super-admin/teachers').then(r=>r.data).catch(()=>({teachers:[]})),
    ]).then(([s,st,t]) => {
      setStats(s); setStudents(st.students||[]); setTeachers(t.teachers||[]);
    }).finally(()=>setLoading(false));
  }, []);

  const addTeacher = async () => {
    if (!tf.name||!tf.email){addToast('Name and email required','error');return;}
    setSaving(true);
    try {
      await API.post('/super-admin/teachers',{...tf,tempPassword:'DeutschBridge@2026'});
      addToast('Teacher created!','success'); setTModal(false);
      setTf({name:'',email:'',phone:'',assignedLevels:['A1','A2','B1','B2']});
      API.get('/super-admin/teachers').then(r=>setTeachers(r.data.teachers||[]));
    } catch(e){addToast(e.message||'Failed','error');}
    finally{setSaving(false);}
  };

  const addStudent = async () => {
    if (!sf.name||!sf.email){addToast('Name and email required','error');return;}
    setSaving(true);
    try {
      await API.post('/super-admin/students',sf);
      addToast('Student registered!','success'); setSModal(false);
      setSf({name:'',email:'',phone:'',level:'A1',scholarshipType:'None',notes:''});
      API.get('/super-admin/students').then(r=>setStudents(r.data.students||[]));
    } catch(e){addToast(e.message||'Failed','error');}
    finally{setSaving(false);}
  };

  return (
    <SuperAdminShell title="Dashboard"
      subtitle={new Date().toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}
      actions={<>
        <button onClick={()=>setSModal(true)} className="px-3 py-2 bg-brand-500 text-white text-xs font-bold rounded-lg hover:bg-brand-600">+ Student</button>
        <button onClick={()=>setTModal(true)} className="px-3 py-2 bg-de-black text-white text-xs font-bold rounded-lg hover:bg-gray-800">+ Teacher</button>
      </>}>

      {loading ? <div className="flex justify-center py-20"><Spinner size="lg" className="text-brand-500"/></div> : (
        <div className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              {label:'Students',value:stats?.totalStudents??'—',icon:'👥',color:'bg-brand-50 text-brand-600'},
              {label:'Teachers',value:stats?.totalTeachers??'—',icon:'👨‍🏫',color:'bg-gray-100 text-de-black'},
              {label:'Revenue (GHS)',value:stats?.totalRevenue?Number(stats.totalRevenue).toLocaleString():'—',icon:'💰',color:'bg-green-50 text-green-700'},
              {label:'Pending Pay',value:stats?.pendingPayments??'—',icon:'💳',color:'bg-yellow-50 text-yellow-700'},
              {label:'Live Exams',value:stats?.activeExams??'—',icon:'📝',color:'bg-purple-50 text-purple-700'},
            ].map(s=>(
              <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <div className="text-xl mb-2">{s.icon}</div>
                <p className={`text-2xl font-bold ${s.color.split(' ')[1]}`}>{s.value}</p>
                <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Students table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-sm font-bold text-de-black">Students ({students.length})</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>{['Student ID','Name','Email','Level','Payment'].map(h=>(
                    <th key={h} className="text-left py-2.5 px-4 text-xs font-bold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {students.map((s,i)=>(
                    <tr key={s.user_id} className={`border-b border-gray-100 hover:bg-gray-50 ${i%2?'bg-gray-50/50':''}`}>
                      <td className="py-2.5 px-4 font-mono text-xs font-bold text-brand-500">
                        {s.student_number||`DB-${new Date().getFullYear()}-${String(s.student_id).padStart(4,'0')}`}
                      </td>
                      <td className="py-2.5 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-brand-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">{s.name?.charAt(0)}</div>
                          <span className="text-xs font-semibold">{s.name}</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-4 text-xs text-gray-500">{s.email}</td>
                      <td className="py-2.5 px-4"><span className="px-2 py-0.5 bg-brand-50 text-brand-600 rounded-full text-xs font-bold">{s.current_level}</span></td>
                      <td className="py-2.5 px-4"><span className={`px-2 py-0.5 rounded-full text-xs font-bold ${s.payment_status==='paid'?'bg-green-100 text-green-700':'bg-yellow-100 text-yellow-700'}`}>{s.payment_status||'pending'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <Modal isOpen={tModal} onClose={()=>setTModal(false)} title="Add Teacher" size="sm">
        <div className="space-y-3">
          {[['Name *','name','text'],['Email *','email','email'],['Phone','phone','text']].map(([l,k,t])=>(
            <div key={k}><label className="block text-xs font-bold mb-1">{l}</label>
            <input type={t} value={tf[k]||''} onChange={e=>setTf(p=>({...p,[k]:e.target.value}))}
              className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"/></div>
          ))}
          <div><label className="block text-xs font-bold mb-1">Levels</label>
          <div className="flex gap-3">{['A1','A2','B1','B2'].map(l=>(
            <label key={l} className="flex items-center gap-1 text-xs font-semibold cursor-pointer">
              <input type="checkbox" checked={tf.assignedLevels.includes(l)}
                onChange={e=>setTf(p=>({...p,assignedLevels:e.target.checked?[...p.assignedLevels,l]:p.assignedLevels.filter(x=>x!==l)}))}/>{l}
            </label>
          ))}</div></div>
          <div className="flex gap-2 justify-end pt-1">
            <Button variant="secondary" size="sm" onClick={()=>setTModal(false)}>Cancel</Button>
            <Button variant="primary" size="sm" loading={saving} onClick={addTeacher}>Create</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={sModal} onClose={()=>setSModal(false)} title="Add Student" size="sm">
        <div className="space-y-3">
          {[['Name *','name','text'],['Email *','email','email'],['Phone','phone','text']].map(([l,k,t])=>(
            <div key={k}><label className="block text-xs font-bold mb-1">{l}</label>
            <input type={t} value={sf[k]||''} onChange={e=>setSf(p=>({...p,[k]:e.target.value}))}
              className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"/></div>
          ))}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-bold mb-1">Level</label>
            <select value={sf.level} onChange={e=>setSf(p=>({...p,level:e.target.value}))} className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none bg-white">
              {['A1','A2','B1','B2'].map(l=><option key={l} value={l}>{l}</option>)}
            </select></div>
            <div><label className="block text-xs font-bold mb-1">Scholarship</label>
            <select value={sf.scholarshipType} onChange={e=>setSf(p=>({...p,scholarshipType:e.target.value}))} className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none bg-white">
              {['None','Full','Partial','Staff'].map(s=><option key={s} value={s}>{s}</option>)}
            </select></div>
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <Button variant="secondary" size="sm" onClick={()=>setSModal(false)}>Cancel</Button>
            <Button variant="primary" size="sm" loading={saving} onClick={addStudent}>Register</Button>
          </div>
        </div>
      </Modal>
    </SuperAdminShell>
  );
}
